// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState, useMemo } from 'react';

// Month-name to numeric-order mapping (PT-BR) for chronological sorting.
const MONTH_ORDER = {
  janeiro: 1, jan: 1,
  fevereiro: 2, fev: 2,
  'março': 3, marco: 3, mar: 3,
  abril: 4, abr: 4,
  maio: 5, mai: 5,
  junho: 6, jun: 6,
  julho: 7, jul: 7,
  agosto: 8, ago: 8,
  setembro: 9, set: 9,
  outubro: 10, out: 10,
  novembro: 11, nov: 11,
  dezembro: 12, dez: 12,
};

// Derive sort metadata from a folder/file name. Returns null when no match.
function getSortOrder(name) {
  const lowerName = (name || '').toLowerCase().trim();

  const trimesterMatch = lowerName.match(/(\d)[ºª°]?\s*[_\-\s]*(trimestre|trim)/i);
  if (trimesterMatch) {
    return { type: 'trimester', order: parseInt(trimesterMatch[1], 10) };
  }

  const yearMatch = lowerName.match(/^ano[_\s\-]*(\d{4})/i);
  if (yearMatch) {
    return { type: 'year', order: parseInt(yearMatch[1], 10) };
  }

  for (const [month, order] of Object.entries(MONTH_ORDER)) {
    if (lowerName.includes(month)) {
      const yearInName = lowerName.match(/(\d{4})/);
      const year = yearInName ? parseInt(yearInName[1], 10) : 0;
      return { type: 'month', order, year };
    }
  }

  return null;
}

function compareByName(nameA, nameB) {
  const orderA = getSortOrder(nameA);
  const orderB = getSortOrder(nameB);

  if (orderA !== null && orderB !== null) {
    if (orderA.type === orderB.type) {
      if (orderA.type === 'month' && orderA.year !== orderB.year) {
        return (orderA.year || 0) - (orderB.year || 0);
      }
      return orderA.order - orderB.order;
    }
    const typeOrder = { year: 1, trimester: 2, month: 3 };
    return typeOrder[orderA.type] - typeOrder[orderB.type];
  }
  if (orderA !== null) return -1;
  if (orderB !== null) return 1;
  return nameA.localeCompare(nameB, 'pt-BR', { sensitivity: 'base' });
}

/**
 * Sort/order state + memoized sorted folders & files for DocumentsPage.
 * Handles natural ordering of "Ano YYYY", "Nº Trimestre" and month names.
 *
 * @param {Array} folders raw folders array
 * @param {Array} files raw files array
 */
export function useDocumentsSort(folders, files) {
  const [sortBy, setSortBy] = useState('name'); // 'name' | 'date' | 'size'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' | 'desc'

  const compare = (a, b, type) => {
    let comparison = 0;
    if (sortBy === 'name') {
      const nameA = type === 'file' ? (a.original_filename || '') : (a.name || '');
      const nameB = type === 'file' ? (b.original_filename || '') : (b.name || '');
      comparison = compareByName(nameA, nameB);
    } else if (sortBy === 'date') {
      comparison = new Date(a.created_at || 0) - new Date(b.created_at || 0);
    } else if (sortBy === 'size' && type === 'file') {
      comparison = (a.size || 0) - (b.size || 0);
    }
    return sortOrder === 'asc' ? comparison : -comparison;
  };

  const sortedFolders = useMemo(
    () => [...folders].sort((a, b) => compare(a, b, 'folder')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [folders, sortBy, sortOrder]
  );

  const sortedFiles = useMemo(
    () => [...files].sort((a, b) => compare(a, b, 'file')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [files, sortBy, sortOrder]
  );

  return { sortBy, setSortBy, sortOrder, setSortOrder, sortedFolders, sortedFiles };
}
